import _ from "lodash";
import { useFormikContext } from "formik";
import { useCallback, useEffect, useState } from "react";
import { DragDropContext, Droppable, DropResult } from "react-beautiful-dnd";
import {
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { CampaignTypes } from "../../App/Enums/CampaignTypes";
import { useNotification } from "../../App/State/Hooks/notificationHooks";
import { useAppSelector } from "../../App/State/Hooks/reduxHooks";
import { selectClientValue } from "../../App/State/Slices/SelectedClientSlice";
import { ICampaignFormState, IFormFieldRecord } from "../../App/Typings/Campaigns";
import { sortFormFields } from "../Helpers/CampaignFunctions";
import ArchivedFieldsModal from "./ArchivedFieldsModal";
import FormPreviewModal from "./FormPreviewModal";
import { FormTableRow } from "./FormTableRow";
import FieldEditModal from "./FieldEditModal";

export default function FormFieldTable() {
  const {
    values: { campaignType, formFields },
    setFieldValue,
  } = useFormikContext<ICampaignFormState>();

  const { hasCareers } = useAppSelector(selectClientValue);
  const { displayWarning } = useNotification();
  const [tableRows, setTableRows] = useState<JSX.Element | JSX.Element[]>([]);

  const formCampaignType =
    campaignType !== CampaignTypes.Form && !hasCareers ? CampaignTypes.TalentNetwork : campaignType;
  const isJobAlertsOnly = formCampaignType === CampaignTypes.JobAlerts;

  useEffect(() => {
    setTableRows(getFormTableRows(formFields, campaignType));
  }, [campaignType, formFields]);

  const onDragEnd = useCallback(
    (result: DropResult) => {
      const source = result.source?.index ?? 0;
      const dest = result.destination?.index ?? 0;

      // dropped outside the list
      if (!result.destination || dest === source) {
        return;
      }

      // no movement
      if (dest === source) {
        return;
      }

      if (campaignType === CampaignTypes.JobAlertsAndTalentNetwork && dest <= 2) {
        displayWarning("All fields must be below the Email, Job Alerts Interest Picker and Opt-In fields.");
        return;
      }

      const newFormFieldOrder = _.cloneDeep(formFields);
      Object.keys(formFields)
        .sort((a, b) => sortFormFields(formFields[a], formFields[b]))
        .forEach((key, index) => {
          // set the new one
          if (index === source) {
            newFormFieldOrder[key].order = dest;
          }
          // moving down
          else if (dest > source) {
            // item is before destination, move it up
            if (index <= dest && index >= source) {
              newFormFieldOrder[key].order -= 1;
            }
            // item is after destination, move it down
            else if (index > dest && index <= source) {
              newFormFieldOrder[key].order += 1;
            }
          }
          // moving up
          else {
            // item is before destination, move it down
            if (index >= dest && index <= source) {
              newFormFieldOrder[key].order += 1;
            }
            // item is after destination move it up
            else if (index < dest && index >= source) {
              newFormFieldOrder[key].order -= 1;
            }
          }
        });
      setFieldValue("formFields", newFormFieldOrder);
      setTableRows(getFormTableRows(newFormFieldOrder, campaignType));
    },
    [campaignType, formFields, setFieldValue, displayWarning],
  );

  return (
    <>
      {isJobAlertsOnly ? (
        <Box sx={{ display: "flex", pb: 2, gap: 2 }}>
          <FormFieldEditTable rows={tableRows} onDragEnd={onDragEnd} />
        </Box>
      ) : (
        <>
          <Paper sx={{ padding: "5px" }}>
            <FormFieldEditTable rows={tableRows} onDragEnd={onDragEnd} />
          </Paper>
          <Box sx={{ mt: "5px", display: "flex" }}>
            <Box>
              <FieldEditModal action="add" />
            </Box>
            <Stack sx={{ marginLeft: "auto" }} direction="row" gap={1}>
              <ArchivedFieldsModal fields={getArchivedFields(formFields)} />
              <FormPreviewModal />
            </Stack>
          </Box>
        </>
      )}
    </>
  );
}

const FormFieldEditTable = ({
  rows,
  onDragEnd,
}: {
  rows: JSX.Element | JSX.Element[];
  onDragEnd: (result: DropResult) => void;
}) => {
  return (
    <TableContainer
      sx={{
        minheight: "500px",
        maxHeight: "700px",
        overflowY: "auto",
      }}
    >
      <Table stickyHeader aria-label="form-table">
        <TableHead>
          <TableRow>
            <TableCell></TableCell>
            <TableCell>Enabled</TableCell>
            <TableCell>Required</TableCell>
            <TableCell>Field Name</TableCell>
            <TableCell>Label</TableCell>
            <TableCell>Field Type</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="table">
            {(provided, snapshot) => (
              <TableBody
                {...provided.droppableProps}
                ref={provided.innerRef}
                style={getListStyle(snapshot.isDraggingOver)}
              >
                {rows}
                {provided.placeholder}
              </TableBody>
            )}
          </Droppable>
        </DragDropContext>
      </Table>
    </TableContainer>
  );
};

const getFormTableRows = (formFields: IFormFieldRecord, campaignType: CampaignTypes) => {
  if (campaignType === CampaignTypes.JobAlerts) {
    return (
      <>
        <FormTableRow
          key={"email"}
          id={"email"}
          fieldName={formFields["email"].fieldName}
          index={formFields["email"].order}
          dragDisabled={true}
        />
        <FormTableRow
          key={"interestPicker"}
          id={"interestPicker"}
          fieldName={formFields["interestPicker"].fieldName}
          index={formFields["interestPicker"].order}
          dragDisabled={true}
        />
      </>
    );
  }

  return Object.entries(formFields)
    .filter(([_, field]) => !field.archived)
    .sort(([_, a], [__, b]) => sortFormFields(a, b))
    .map(([key, field], index) => {
      return (
        <FormTableRow
          key={key}
          id={key}
          fieldName={field.fieldName}
          index={field.order ?? index}
          dragDisabled={
            key === "interestPicker" ||
            key === "optIn" ||
            (campaignType === CampaignTypes.JobAlertsAndTalentNetwork && key === "email")
          }
          rowHidden={hideJobAlertsFields(campaignType, key)}
        />
      );
    });
};

const hideJobAlertsFields = (campaignType: CampaignTypes, key: string) =>
  (campaignType === CampaignTypes.Form || campaignType === CampaignTypes.TalentNetwork) &&
  (key === "interestPicker" || key === "optIn");

const getListStyle = (isDraggingOver: boolean) => ({
  background: isDraggingOver ? "rgb(158, 224, 186)" : "lightgrey",
  padding: "10px",
});

const getArchivedFields = (formFields: IFormFieldRecord) => {
  const archivedFields = {} as IFormFieldRecord;
  for (let i = 0; i < Object.keys(formFields).length; i++) {
    const key = Object.keys(formFields)[i];
    if (formFields[key].archived) {
      archivedFields[key] = formFields[key];
    }
  }

  return archivedFields;
};
